import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { services } from '../data'
import Lightbox from '../components/Lightbox'
import SubpageHero from '../components/SubpageHero'
import useMeta from '../hooks/useMeta'

export default function Housekeeping() {
  useMeta('Housekeeping', 'Eksklusiv housekeeping og ejendomsservice til private hjem. Oplev hotel-standard pleje, opredning, tøjvask og strygning i hverdagen.')

  const [galleryImages, setGalleryImages] = useState([])
  const [lightboxState, setLightboxState] = useState({ isOpen: false, index: 0 })
  const data = services.housekeeping

  useEffect(() => {
    // Fetch gallery from CMS
    fetch('https://api.dev.auxo.dk/public/content/galleri')
      .then((res) => res.json())
      .then((resData) => {
        if (Array.isArray(resData)) {
          const gallery = resData.find((g) => g.gallery_slug === data.slug)
          if (gallery && gallery.images) {
            setGalleryImages(gallery.images)
          }
        }
      })
      .catch(() => {
        // Fallback images
        setGalleryImages([
          'https://assets.auxodev.dk/assets/682e08ab-395d-4160-a58a-7bbf165ed9e3/housekeeping-hotel-apartment.jpg',
          'https://assets.auxodev.dk/assets/682e08ab-395d-4160-a58a-7bbf165ed9e3/residential-cleaning-living-room.jpg'
        ])
      })
  }, [data.slug])

  const heroImages = [
    'https://assets.auxodev.dk/assets/682e08ab-395d-4160-a58a-7bbf165ed9e3/housekeeping-hotel-apartment.jpg',
    'https://assets.auxodev.dk/assets/682e08ab-395d-4160-a58a-7bbf165ed9e3/scandinavian-luxury-living-room-clean.jpg',
    'https://assets.auxodev.dk/assets/682e08ab-395d-4160-a58a-7bbf165ed9e3/residential-cleaning-living-room.jpg'
  ]

  return (
    <div className="flex flex-col min-h-screen bg-brand-light">
      {/* Hero Section */}
      <SubpageHero
        title={data.title}
        intro={data.intro}
        images={heroImages}
      />

      {/* Main Content */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column: Description & Tasks */}
          <div className="lg:col-span-7 space-y-12 text-left">
            <div>
              <h2 className="text-2xl font-bold text-brand-navy tracking-tight">Eksklusiv pleje og vedligeholdelse af dit hjem</h2>
              <p className="mt-4 text-brand-text-muted leading-relaxed">
                {data.description}
              </p>
            </div>

            {/* Typical Tasks */}
            <div className="border border-brand-border bg-white p-6 md:p-8 shadow-sm">
              <h3 className="text-lg font-bold text-brand-navy flex items-center gap-2.5">
                Hvad indebærer vores housekeeping?
              </h3>
              <ul className="mt-6 space-y-3.5 text-sm text-brand-text-muted">
                {data.tasks.map((task, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <i className="fa-solid fa-check text-black mt-0.5 shrink-0"></i>
                    <span>{task}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right Column: Advantages & CTA */}
          <div className="lg:col-span-5 space-y-8 text-left">
            {/* Advantages */}
            <div className="border border-brand-border bg-brand-gray/30 p-6 md:p-8">
              <h3 className="text-lg font-bold text-brand-navy">Fordele ved vores housekeeping</h3>
              <ul className="mt-6 space-y-4 text-sm text-brand-text-muted">
                {data.advantages.map((adv, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <i className="fa-solid fa-star text-black mt-0.5 shrink-0"></i>
                    <span className="font-medium text-brand-text-dark">{adv}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* CTA Box */}
            <div className="bg-brand-navy text-white p-6 md:p-8 text-center relative overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,rgba(0,102,255,0.15),transparent_50%)]" />
              <h3 className="text-xl font-bold relative z-10">Ønsker du den ultimative komfort?</h3>
              <p className="mt-3 text-sm text-slate-300 leading-relaxed relative z-10">
                Kontakt os i dag for at høre mere om vores eksklusive housekeeping-aftaler.
              </p>
              <div className="mt-6 relative z-10">
                <Link
                  to="/kontakt"
                  className="inline-block w-full bg-white py-3 text-sm font-bold text-brand-navy transition-all duration-200 hover:bg-brand-gray hover:shadow-md"
                >
                  Få et tilbud nu
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      {galleryImages.length > 0 && (
        <section className="py-16 bg-brand-gray/30 border-t border-brand-border">
          <div className="mx-auto max-w-7xl px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-brand-navy tracking-tight">Galleri</h2>
            <p className="mt-3 text-brand-text-muted max-w-2xl mx-auto text-sm leading-relaxed">
              Se billeder af vores housekeeping-arbejde. Klik på et billede for at se det i fuld størrelse.
            </p>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {galleryImages.map((img, index) => (
                <div
                  key={index}
                  onClick={() => setLightboxState({ isOpen: true, index })}
                  className="group relative overflow-hidden border border-brand-border bg-white shadow-sm cursor-pointer h-64"
                >
                  <img
                    src={img}
                    alt={`Housekeeping galleri ${index + 1}`}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                    <i className="fa-solid fa-magnifying-glass-plus text-white text-2xl"></i>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Lightbox */}
      {lightboxState.isOpen && (
        <Lightbox
          images={galleryImages}
          currentIndex={lightboxState.index}
          onClose={() => setLightboxState({ isOpen: false, index: 0 })}
          onNavigate={(index) => setLightboxState((prev) => ({ ...prev, index }))}
        />
      )}
    </div>
  )
}
